import { useCallback, useEffect, useMemo, useState } from 'react';
import momentTimezone from 'moment-timezone';
import { Dialog } from '@headlessui/react';

import { Alert, Button, Text, TextField } from 'components';
import { ManagementSelectFilter } from 'components/ManagementFilters';
import { Pill } from 'components/ManagementCard';
import { Modal } from 'components/Modal';
import { pageCardClasses } from 'components/AppLayout/styles';
import {
  commonStrings,
  sendHistoryStrings,
  strings,
  summaryManagementStrings,
} from 'constants/strings';
import { useIsDesktop, useSummary, useSummaryDetail } from 'hooks';
import useToast from 'hooks/useToast';
import type {
  TLegacySummaryRow,
  TSummaryDetail,
  TSummaryMode,
  TSummaryRow,
  TSummarySourceType,
} from 'types/summary';
import MessagePreview from '../SendMessageForAll/MessagePreview';
import { ClickedUsersModal } from './ClickedUsersModal';
import { SummaryCardList } from './SummaryCardList';
import { buildSummaryPreviewData } from './summaryPreview';
import {
  formatSummaryClickRate,
  formatSummaryCount,
  resolveUnavailableRecipientBadge,
  resolveUnknownRecipientBadge,
  summaryStatusTone,
} from './summaryDisplay';
import { SummaryTable } from './SummaryTable';

const PAGE_SIZE = 10;
const YEAR_MIN = 2000;
const SOURCE_TYPE_ORDER: TSummarySourceType[] = [
  'BULK',
  'CHAT_NOTIFICATION',
  'GREETING',
  'AUTO_REPLY',
  // 「すべて」には含まれず、明示指定でだけ引ける
  'TEST',
];
const SOURCE_TYPE_OPTIONS = SOURCE_TYPE_ORDER.map((sourceType) => ({
  value: sourceType,
  label: summaryManagementStrings.sourceTypes[sourceType],
}));


function yearToFromTo(year: number): { from: string; to: string } {
  const y = Number.isFinite(year)
    ? Math.max(YEAR_MIN, year)
    : momentTimezone().tz('Asia/Tokyo').year();
  return { from: `${y}-01-01`, to: `${y}-12-31` };
}

function isLegacyRow(
  row: TSummaryRow | TLegacySummaryRow,
): row is TLegacySummaryRow {
  return 'sentAt' in row;
}

function formatDate(iso: string): string {
  return momentTimezone(iso)
    .tz('Asia/Tokyo')
    .format('YYYY年M月D日 HH:mm');
}

function formatNumber(value: number | null): string {
  return value == null
    ? summaryManagementStrings.unavailableMetric
    : value.toLocaleString('ja-JP');
}

/**
 * 送信履歴から旧開封アーカイブとして開く場合は initialMode と onBack を渡す。
 * onBack があるときはクリック集計へ戻らず、送信履歴へ戻す。
 */
export default function SummaryManagement({
  initialMode = 'CLICK',
  onBack,
}: {
  initialMode?: TSummaryMode;
  onBack?: () => void;
} = {}) {
  const { toastError } = useToast();
  const currentYear = momentTimezone().tz('Asia/Tokyo').year();
  const [yearInput, setYearInput] = useState(String(currentYear));
  const [dateRange, setDateRange] = useState(() => yearToFromTo(currentYear));
  const [mode, setMode] = useState<TSummaryMode>(initialMode);
  const [sourceType, setSourceType] = useState<TSummarySourceType | ''>('');
  const [selectedRowId, setSelectedRowId] = useState<string | null>(null);
  const [clickedUsersOpen, setClickedUsersOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  const {
    data: summary,
    loading,
    isRefetching,
    error,
    refetch: refetchSummary,
  } = useSummary({
    mode,
    ...dateRange,
    currentPage,
    pageSize: PAGE_SIZE,
    ...(mode === 'CLICK' && sourceType ? { sourceType } : {}),
  });
  const {
    data: selectedDetail,
    loading: detailLoading,
    isRefetching: detailRefetching,
    error: detailError,
    refetch: refetchDetail,
  } = useSummaryDetail(mode === 'CLICK' ? selectedRowId : null);

  const rows = useMemo(() => summary?.data ?? [], [summary?.data]);
  const total = summary?.total ?? 0;
  const totalPage = summary?.totalPage ?? 0;
  const selectedLegacyRow = useMemo(
    () =>
      mode === 'LEGACY_OPEN'
        ? rows.find(
            (row): row is TLegacySummaryRow =>
              isLegacyRow(row) && row.id === selectedRowId,
          ) ?? null
        : null,
    [mode, rows, selectedRowId],
  );
  const selectedLegacyPreview = useMemo(
    () => buildSummaryPreviewData(selectedLegacyRow?.sections ?? []),
    [selectedLegacyRow],
  );
  const countLabel = `${total}${commonStrings.countSuffix}`;
  const pageLabel = totalPage > 0 ? `${currentPage} / ${totalPage}` : '0 / 0';
  const canMovePrev = !loading && currentPage > 1;
  const canMoveNext = !loading && totalPage > 0 && currentPage < totalPage;

  useEffect(() => {
    if (error) toastError(new Error(error));
  }, [error, toastError]);

  const resetSelection = useCallback(() => {
    setSelectedRowId(null);
    setClickedUsersOpen(false);
  }, []);
  const openClickedUsers = useCallback(() => setClickedUsersOpen(true), []);

  const handleSearch = useCallback(() => {
    const parsed = parseInt(yearInput.trim(), 10);
    const effectiveYear =
      Number.isNaN(parsed) || parsed < YEAR_MIN || parsed > currentYear
        ? currentYear
        : parsed;
    const nextDateRange = yearToFromTo(effectiveYear);
    const queryIsUnchanged =
      currentPage === 1 &&
      dateRange.from === nextDateRange.from &&
      dateRange.to === nextDateRange.to;

    setYearInput(String(effectiveYear));
    setDateRange(nextDateRange);
    setCurrentPage(1);
    resetSelection();
    // 条件変更時はquery keyの変更で取得し、同じ条件なら明示的に再取得する。
    if (queryIsUnchanged) refetchSummary();
  }, [
    currentPage,
    currentYear,
    dateRange.from,
    dateRange.to,
    refetchSummary,
    resetSelection,
    yearInput,
  ]);

  const handleReload = useCallback(() => {
    refetchSummary();
    if (mode === 'CLICK' && selectedRowId) refetchDetail();
  }, [mode, refetchDetail, refetchSummary, selectedRowId]);

  const handleSourceTypeChange = useCallback(
    (nextSourceType: TSummarySourceType | '') => {
      setSourceType(nextSourceType);
      setCurrentPage(1);
      resetSelection();
    },
    [resetSelection],
  );

  const handleModeChange = useCallback(
    (nextMode: TSummaryMode) => {
      if (nextMode === 'CLICK' && onBack) {
        onBack();
        return;
      }
      setMode(nextMode);
      setSourceType('');
      setCurrentPage(1);
      resetSelection();
    },
    [resetSelection, onBack],
  );

  const handleSelect = useCallback(
    (row: TSummaryRow | TLegacySummaryRow) => setSelectedRowId(row.id),
    [],
  );

  // 詳細はlg以上では右の常設pane、lg未満ではmodalで出す。
  // CSSだけで出し分けると一覧10件の下へ回り、選択しても画面が動かず気付けない。
  const isDesktop = useIsDesktop();

  const showLegacyArchiveLink =
    mode === 'CLICK' && Boolean(summary?.hasLegacyArchive);

  return (
    <>
      <section className={`flex min-h-[calc(100vh-12rem)] w-full flex-col ${pageCardClasses.regular}`}>
        <header className="mb-6 flex flex-shrink-0 flex-wrap items-start justify-between gap-5">
          <div className="grid gap-3">
            <Text bold className="text-2xl">
              {onBack
                ? summaryManagementStrings.archive.open
                : summaryManagementStrings.title}
            </Text>
            <p className="max-w-xl text-xs text-muted">
              {mode === 'CLICK'
                ? summaryManagementStrings.description
                : summaryManagementStrings.archive.description}
            </p>
            {/* gridの列幅はtitleと説明文で決まるため、指定しないとlabelに対して不自然に伸びる。 */}
            {showLegacyArchiveLink && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="justify-self-start border-accent text-accent hover:border-accent hover:bg-accent-soft"
                onClick={() => handleModeChange('LEGACY_OPEN')}
              >
                {summaryManagementStrings.archive.open}
              </Button>
            )}
            {mode === 'LEGACY_OPEN' && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="justify-self-start border-accent text-accent hover:border-accent hover:bg-accent-soft"
                onClick={() => handleModeChange('CLICK')}
              >
                {onBack
                  ? sendHistoryStrings.backToHistory
                  : summaryManagementStrings.archive.back}
              </Button>
            )}
          </div>

          <div className="flex w-full flex-wrap items-end justify-between gap-3 sm:w-auto sm:justify-start">
            <div
              className="flex flex-wrap items-end gap-3"
              title={
                mode === 'CLICK'
                  ? summaryManagementStrings.search.clickSearchHint
                  : summaryManagementStrings.search.legacyOpenSearchHint
              }
            >
              <div className="flex items-center gap-2 pb-0.5">
                <label
                  htmlFor="year"
                  className="whitespace-nowrap text-sm text-muted"
                >
                  {summaryManagementStrings.search.deliveryYearLabel}
                </label>
                <TextField
                  name="year"
                  type="number"
                  value={yearInput}
                  min={YEAR_MIN}
                  max={currentYear}
                  onChange={(event) => setYearInput(event.target.value)}
                />
                <span className="text-sm text-muted">
                  {summaryManagementStrings.search.yearSuffix}
                </span>
              </div>
              {mode === 'CLICK' && (
                <ManagementSelectFilter
                  className="min-w-[190px]"
                  label={summaryManagementStrings.search.sourceTypeLabel}
                  value={sourceType}
                  options={SOURCE_TYPE_OPTIONS}
                  onChange={handleSourceTypeChange}
                  onClear={() => handleSourceTypeChange('')}
                />
              )}
              <Button
                type="button"
                size="sm"
                onClick={handleSearch}
                disabled={loading}
              >
                {summaryManagementStrings.search.searchButton}
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleReload}
                disabled={loading || isRefetching || detailRefetching}
              >
                {summaryManagementStrings.search.reloadButton}
              </Button>
            </div>
            <span className="rounded-full border border-border/70 bg-white px-3 py-1 text-xs font-semibold text-muted">
              {countLabel}
              {isRefetching ? ` · ${summaryManagementStrings.refreshing}` : ''}
            </span>
            {totalPage > 1 && (
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setCurrentPage((page) => Math.max(1, page - 1));
                    resetSelection();
                  }}
                  disabled={!canMovePrev}
                >
                  {strings.common.pagination.previous}
                </Button>
                <span className="min-w-[86px] text-center text-xs font-semibold text-muted">
                  {pageLabel} {summaryManagementStrings.paginationPage}
                </span>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setCurrentPage((page) => Math.min(totalPage, page + 1));
                    resetSelection();
                  }}
                  disabled={!canMoveNext}
                >
                  {strings.common.pagination.next}
                </Button>
              </div>
            )}
          </div>
        </header>

        {error && (
          <div className="mb-6">
            <Alert>{error}</Alert>
          </div>
        )}

        <div className="flex min-h-0 flex-1 flex-col gap-6 lg:flex-row lg:gap-8">
          <div className="min-w-0 flex-1 overflow-auto">
            <div className="table-scroll hidden overflow-x-auto rounded-2xl border border-border/70 bg-white lg:block">
              <SummaryTable
                mode={mode}
                rows={rows}
                loading={loading}
                hasError={Boolean(error)}
                onSelect={handleSelect}
              />
            </div>
            <div className="lg:hidden">
              <SummaryCardList
                mode={mode}
                rows={rows}
                loading={loading}
                hasError={Boolean(error)}
                onSelect={handleSelect}
                className="md:grid-cols-2"
              />
            </div>
          </div>

          {isDesktop &&
            (mode === 'CLICK' ? (
              <SummaryDetailPanel
                selected={Boolean(selectedRowId)}
                detail={selectedDetail}
                loading={detailLoading}
                error={detailError}
                onOpenClickedUsers={openClickedUsers}
              />
            ) : (
              <LegacyPreviewPanel
                selected={Boolean(selectedLegacyRow)}
                sections={selectedLegacyPreview.sections}
                assets={selectedLegacyPreview.assets}
              />
            ))}
        </div>

        {/* 詳細と一覧はモバイルで兄弟Dialogになる。Headless UIの多重ダイアログ制御は
            ネストしたDialogにしか効かず、同時openだと一覧パネル内のタップが詳細側の
            外側クリックと判定されてresetSelectionが走り両方閉じる。常に1枚だけopenにする。
            selectedRowIdは保持するので、一覧を閉じれば詳細へ戻る。 */}
        {!isDesktop && (
          <SummaryDetailModal
            open={
              Boolean(mode === 'CLICK' ? selectedRowId : selectedLegacyRow) &&
              !clickedUsersOpen
            }
            onClose={resetSelection}
            mode={mode}
            detail={selectedDetail}
            loading={detailLoading}
            error={detailError}
            legacySections={selectedLegacyPreview.sections}
            legacyAssets={selectedLegacyPreview.assets}
            onOpenClickedUsers={openClickedUsers}
          />
        )}

        <ClickedUsersModal
          dispatchId={selectedRowId}
          open={clickedUsersOpen}
          onClose={() => setClickedUsersOpen(false)}
        />
      </section>
    </>
  );
}

/**
 * lg未満で詳細をmodalとして出す。desktopの常設paneと同じ内容を、
 * 一覧を遡らずに読めて明示的に閉じられる形にする。
 */
function SummaryDetailModal({
  open,
  onClose,
  mode,
  detail,
  loading,
  error,
  legacySections,
  legacyAssets,
  onOpenClickedUsers,
}: {
  open: boolean;
  onClose: () => void;
  mode: TSummaryMode;
  detail: TSummaryDetail | undefined;
  loading: boolean;
  error: string | undefined;
  legacySections: Parameters<typeof MessagePreview>[0]['sections'];
  legacyAssets: Parameters<typeof MessagePreview>[0]['assets'];
  onOpenClickedUsers: () => void;
}) {
  return (
    <Modal open={open} onClose={onClose} size="lg">
      <Dialog.Title as="h2" className="mb-3 text-lg font-bold text-ink">
        {mode === 'CLICK'
          ? summaryManagementStrings.detail.title
          : summaryManagementStrings.table.contentConfirm}
      </Dialog.Title>
      <div className="max-h-[65vh] overflow-y-auto">
        {mode === 'CLICK' ? (
          <>
            {loading && (
              <p className="text-sm text-muted">
                {summaryManagementStrings.detail.loading}
              </p>
            )}
            {error && <Alert>{error}</Alert>}
            {detail && !loading && !error && (
              <SummaryDetailContent
                detail={detail}
                onOpenClickedUsers={onOpenClickedUsers}
              />
            )}
          </>
        ) : (
          <div className="relative mx-auto flex min-h-[460px] max-h-[460px] w-full max-w-[260px] flex-col overflow-hidden rounded-[3rem] border-8 border-white bg-white shadow-2xl sm:min-h-[540px] sm:max-h-[540px] sm:max-w-[300px]">
            <MessagePreview sections={legacySections} assets={legacyAssets} />
          </div>
        )}
      </div>
      <div className="mt-4">
        <Button
          type="button"
          variant="outline"
          onClick={onClose}
          className="w-full"
        >
          {commonStrings.close}
        </Button>
      </div>
    </Modal>
  );
}

function SummaryDetailPanel({
  selected,
  detail,
  loading,
  error,
  onOpenClickedUsers,
}: {
  selected: boolean;
  detail: TSummaryDetail | undefined;
  loading: boolean;
  error: string | undefined;
  onOpenClickedUsers: () => void;
}) {
  return (
    <aside
      className={`${selected ? 'flex' : 'hidden lg:flex'} w-full flex-shrink-0 flex-col gap-4 lg:w-[375px]`}
      aria-label={summaryManagementStrings.detail.title}
    >
      <section className="rounded-2xl border border-border/70 bg-white p-4">
        <Text bold>{summaryManagementStrings.detail.title}</Text>
        {!selected && (
          <p className="mt-3 text-sm text-muted">
            {summaryManagementStrings.detail.emptyHint}
          </p>
        )}
        {selected && loading && (
          <p className="mt-3 text-sm text-muted">
            {summaryManagementStrings.detail.loading}
          </p>
        )}
        {selected && error && (
          <div className="mt-3">
            <Alert>{error}</Alert>
          </div>
        )}
        {selected && detail && !loading && !error && (
          <SummaryDetailContent
            detail={detail}
            onOpenClickedUsers={onOpenClickedUsers}
          />
        )}
      </section>
    </aside>
  );
}

function SummaryDetailContent({
  detail,
  onOpenClickedUsers,
}: {
  detail: TSummaryDetail;
  onOpenClickedUsers: () => void;
}) {
  const unavailableBadge = resolveUnavailableRecipientBadge(detail);
  // 一覧と同じ導出を使う。人数を集計できないUNKNOWNの注記(A42)が
  // 詳細にだけ出ない、という不一致を作らない。
  const unknownBadge = resolveUnknownRecipientBadge(detail);
  return (
    <div className="mt-3 grid gap-4">
      <div className="grid gap-2 rounded-xl bg-surface p-3">
        <p className="text-xs text-muted">{formatDate(detail.startedAt)}</p>
        <p className="text-sm font-semibold text-ink">
          {summaryManagementStrings.sourceTypes[detail.sourceType]}
        </p>
        {detail.sourceLabel && (
          <p className="text-xs text-muted">{detail.sourceLabel}</p>
        )}
        <div className="flex flex-wrap gap-1">
          <Pill tone={summaryStatusTone[detail.status]}>
            {summaryManagementStrings.deliveryStatus[detail.status]}
          </Pill>
          {unavailableBadge && <Pill tone="warning">{unavailableBadge}</Pill>}
          {unknownBadge && <Pill tone="warning">{unknownBadge}</Pill>}
        </div>
        <dl className="grid grid-cols-2 gap-2">
          <div>
            <dt className="text-[10px] text-muted">
              {summaryManagementStrings.table.sentRecipientCount}
            </dt>
            <dd className="text-sm text-ink">
              {formatNumber(detail.sentRecipientCount)}
            </dd>
          </div>
          <div>
            <dt className="text-[10px] text-muted">
              {summaryManagementStrings.table.totalClicks}
            </dt>
            <dd className="text-sm text-ink">
              {formatNumber(detail.totalClicks)}
            </dd>
          </div>
          <div>
            <dt className="text-[10px] text-muted">
              {summaryManagementStrings.detail.uniqueClickRecipientCount}
            </dt>
            <dd className="text-sm text-ink">
              {formatSummaryCount(detail.uniqueClickRecipientCount)}
            </dd>
          </div>
          <div>
            <dt className="text-[10px] text-muted">
              {summaryManagementStrings.detail.clickRate}
            </dt>
            <dd className="text-sm text-ink">
              {formatSummaryClickRate(detail.clickRate)}
            </dd>
          </div>
        </dl>
        {detail.coverage !== 'FULL' && (
          <p
            className="text-xs font-semibold text-amber-800"
            title={summaryManagementStrings.coverageHints[detail.coverage]}
          >
            {summaryManagementStrings.coverageWarnings[detail.coverage]}
          </p>
        )}
        {detail.recipientTrackingSupported ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={onOpenClickedUsers}
          >
            {summaryManagementStrings.detail.openClickedUsers.replace(
              '$count',
              (detail.uniqueClickRecipientCount ?? 0).toLocaleString('ja-JP'),
            )}
          </Button>
        ) : (
          <p className="text-xs text-muted">
            {summaryManagementStrings.detail.unsupportedNotice}
          </p>
        )}
      </div>

      <section>
        <Text bold>{summaryManagementStrings.detail.linksTitle}</Text>
        {detail.links.length === 0 ? (
          <p className="mt-2 text-sm text-muted">
            {summaryManagementStrings.detail.noLinks}
          </p>
        ) : (
          <ul className="mt-2 grid gap-2">
            {detail.links.map((link) => (
              <li key={link.id} className="rounded-xl bg-surface p-3">
                <dl className="grid gap-2">
                  <div>
                    <dt className="text-[10px] text-muted">
                      {summaryManagementStrings.detail.displayUrl}
                    </dt>
                    <dd className="break-all text-xs text-ink">
                      {link.displayUrl}
                    </dd>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <dt className="text-[10px] text-muted">
                        {summaryManagementStrings.detail.totalClicks}
                      </dt>
                      <dd className="text-sm text-ink">
                        {formatNumber(link.totalClicks)}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-[10px] text-muted">
                        {
                          summaryManagementStrings.detail
                            .uniqueClickRecipientCount
                        }
                      </dt>
                      <dd className="text-sm text-ink">
                        {formatSummaryCount(link.uniqueClickRecipientCount)}
                      </dd>
                    </div>
                  </div>
                </dl>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

function LegacyPreviewPanel({
  selected,
  sections,
  assets,
}: {
  selected: boolean;
  sections: Parameters<typeof MessagePreview>[0]['sections'];
  assets: Parameters<typeof MessagePreview>[0]['assets'];
}) {
  return (
    <aside
      className={`${selected ? 'flex' : 'hidden lg:flex'} w-full flex-shrink-0 flex-col gap-4 lg:w-[375px]`}
      title={summaryManagementStrings.table.contentConfirmHint}
    >
      {selected ? (
        <div className="relative mx-auto flex min-h-[460px] max-h-[460px] w-full max-w-[260px] flex-1 flex-col overflow-hidden rounded-[3rem] border-8 border-white bg-white shadow-2xl sm:min-h-[540px] sm:max-h-[540px] sm:max-w-[300px] lg:min-h-[700px] lg:max-h-[700px] lg:max-w-[375px]">
          <MessagePreview sections={sections} assets={assets} />
        </div>
      ) : (
        <section className="rounded-2xl border border-border/70 bg-white p-4 text-sm text-muted">
          {summaryManagementStrings.table.contentConfirmHint}
        </section>
      )}
    </aside>
  );
}
